import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Empty, Skeleton, Typography, Row, Col } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

import { portalService } from '../../services/api';
import ResponsavelFrequenciaCard from './ResponsavelFrequenciaCard';
import '../../styles/ResponsavelFrequencia.css';

dayjs.locale('pt-br');

const { Title, Text } = Typography;

const resolverErroResumo = (error) => {
  const status = error?.response?.status;
  if (status === 403) return 'Você não possui acesso a esta área.';
  if (status === 422) return 'Não foi possível consultar o período informado.';
  return 'Não foi possível carregar a frequência. Tente novamente.';
};

const ResponsavelFrequencia = () => {
  const navigate = useNavigate();
  const [mesReferencia, setMesReferencia] = useState(() => dayjs().startOf('month'));
  const [resumos, setResumos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  const carregarResumo = useCallback(async (mes) => {
    setLoading(true);
    setErro('');
    try {
      const response = await portalService.listarFrequencias({
        dataInicio: mes.startOf('month').format('YYYY-MM-DD'),
        dataFim: mes.endOf('month').format('YYYY-MM-DD'),
      });
      setResumos(response.data || []);
    } catch (error) {
      setErro(resolverErroResumo(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarResumo(mesReferencia);
  }, [mesReferencia, carregarResumo]);

  const grupos = useMemo(() => {
    const mapa = new Map();
    resumos.forEach((item) => {
      const chave = item.aluno_id;
      if (!mapa.has(chave)) {
        mapa.set(chave, { alunoNome: item.aluno_nome, itens: [] });
      }
      mapa.get(chave).itens.push(item);
    });
    return Array.from(mapa.values());
  }, [resumos]);

  const irParaMesAnterior = () => setMesReferencia((atual) => atual.subtract(1, 'month'));
  const irParaMesSeguinte = () => setMesReferencia((atual) => atual.add(1, 'month'));
  const irParaMesAtual = () => setMesReferencia(dayjs().startOf('month'));

  const handleVerDetalhes = (matriculaId) => {
    navigate(`/responsavel/frequencia/${matriculaId}?mes=${mesReferencia.format('YYYY-MM')}`);
  };

  return (
    <div className="responsavel-frequencia">
      <Title level={3} className="responsavel-frequencia__titulo">
        Frequência
      </Title>

      <div className="responsavel-frequencia__filtro-periodo" role="group" aria-label="Filtro de período">
        <Button
          icon={<LeftOutlined aria-hidden="true" />}
          onClick={irParaMesAnterior}
          aria-label="Mês anterior"
        />
        <Text strong className="responsavel-frequencia__mes-atual">
          {mesReferencia.format('MMMM [de] YYYY')}
        </Text>
        <Button
          icon={<RightOutlined aria-hidden="true" />}
          onClick={irParaMesSeguinte}
          aria-label="Mês seguinte"
        />
        <Button onClick={irParaMesAtual}>Mês atual</Button>
      </div>

      {loading && (
        <div role="status" aria-live="polite" className="responsavel-frequencia__loading">
          <Text type="secondary">Carregando frequência…</Text>
          <Skeleton active paragraph={{ rows: 3 }} style={{ marginTop: 12 }} />
        </div>
      )}

      {!loading && erro && (
        <Card className="responsavel-frequencia__erro">
          <Text>{erro}</Text>
          <div className="responsavel-frequencia__erro-acoes">
            <Button type="primary" onClick={() => carregarResumo(mesReferencia)}>
              Tentar novamente
            </Button>
          </div>
        </Card>
      )}

      {!loading && !erro && grupos.length === 0 && (
        <Empty description="Não foram encontradas matrículas vinculadas." />
      )}

      {!loading && !erro && grupos.length > 0 && grupos.map((grupo) => (
        <div key={grupo.alunoNome + grupo.itens[0]?.aluno_id} className="responsavel-frequencia__grupo-aluno">
          <Title level={4} className="responsavel-frequencia__aluno-nome">
            {grupo.alunoNome}
          </Title>
          <Row gutter={[16, 16]}>
            {grupo.itens.map((item) => (
              <Col xs={24} md={12} lg={8} key={item.matricula_id}>
                <ResponsavelFrequenciaCard item={item} onVerDetalhes={handleVerDetalhes} />
              </Col>
            ))}
          </Row>
        </div>
      ))}
    </div>
  );
};

export default ResponsavelFrequencia;
